import type { Editor } from "@tiptap/core";
import type { MutableRefObject } from "react";

/**
 * TipTap HTML often round-trips differently from the controlled React value
 * (empty <p>, <br>, whitespace). Calling setContent on every parent re-render
 * rebuilds the ProseMirror doc, jumps/kills the caret, and on Safari/WebKit
 * can freeze the email composer under rapid keystrokes.
 *
 * Track the last HTML we pushed to React via onUpdate. When the parent echoes
 * that same string back as `value`/`content`, skip setContent. Only apply
 * truly external updates (signature, Martina draft, form reset).
 */
export function rememberTipTapEmittedHtml(
  lastEmittedHtmlRef: MutableRefObject<string | null>,
  html: string,
): void {
  lastEmittedHtmlRef.current = html;
}

export function syncTipTapExternalContent(
  editor: Editor | null | undefined,
  externalHtml: string | null | undefined,
  lastEmittedHtmlRef: MutableRefObject<string | null>,
): void {
  if (!editor || editor.isDestroyed) return;
  const next = externalHtml ?? "";
  if (lastEmittedHtmlRef.current !== null && next === lastEmittedHtmlRef.current) {
    return;
  }
  if (next === editor.getHTML()) {
    lastEmittedHtmlRef.current = next;
    return;
  }
  // emitUpdate=false: do not bounce back into React onChange
  editor.commands.setContent(next, { emitUpdate: false });
  lastEmittedHtmlRef.current = next;
}
